const cleanLabel = label =>
  String(label || "")
    .replace(/\s*\*+\s*$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

const formatList = labels => {
  const visibleLabels = labels.slice(0, 4);
  const remainingCount = labels.length - visibleLabels.length;

  if (remainingCount > 0) {
    return `${visibleLabels.join(", ")}, and ${remainingCount} more`;
  }
  if (visibleLabels.length === 2) {
    return visibleLabels.join(" and ");
  }
  if (visibleLabels.length > 2) {
    return `${visibleLabels.slice(0, -1).join(", ")}, and ${
      visibleLabels[visibleLabels.length - 1]
    }`;
  }
  return visibleLabels[0] || "";
};

export const getRequiredFieldsMessage = labels => {
  const uniqueLabels = labels
    .map(cleanLabel)
    .filter(Boolean)
    .filter((label, index, values) => values.indexOf(label) === index);

  if (!uniqueLabels.length) {
    return "Please fill in all required fields before continuing.";
  }

  return uniqueLabels.length === 1
    ? `Please fill in the required field: ${uniqueLabels[0]}.`
    : `Please fill in the following required fields: ${formatList(
        uniqueLabels
      )}.`;
};

const isEmptyValue = value =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const getFieldLabel = field =>
  cleanLabel(
    field?.data?.validationLabel ||
      field?.data?.name ||
      field?.data?.label ||
      field?.data?.placeholder
  );

export const getBaseModalValidationMessage = invalidFields => {
  const namedFields = invalidFields
    .map(field => ({
      label: getFieldLabel(field),
      missing: isEmptyValue(field?.data?.value)
    }))
    .filter(field => field.label)
    .filter(
      (field, index, fields) =>
        fields.findIndex(candidate => candidate.label === field.label) === index
    );

  if (!namedFields.length) {
    return getRequiredFieldsMessage([]);
  }

  const labels = namedFields.map(field => field.label);
  const allMissing = namedFields.every(field => field.missing);
  const noneMissing = namedFields.every(field => !field.missing);

  if (allMissing) {
    return getRequiredFieldsMessage(labels);
  }

  if (noneMissing) {
    return labels.length === 1
      ? `Please enter a valid value for ${labels[0]}.`
      : `Please enter valid values for the following fields: ${formatList(
          labels
        )}.`;
  }

  return `Please fill in or correct the following fields: ${formatList(
    labels
  )}.`;
};

const getElementLabel = element => {
  const label =
    element?.getAttribute?.("data-validation-label") ||
    element?.getAttribute?.("aria-label") ||
    element?.getAttribute?.("title") ||
    element?.labels?.[0]?.textContent ||
    element?.getAttribute?.("placeholder") ||
    element?.getAttribute?.("name");

  return cleanLabel(label) || "this field";
};

export const getLegacyFieldValidationMessage = (element, validation) => {
  const label = getElementLabel(element);

  switch (validation) {
    case "isValidEmail":
      return `Please enter a valid email address for ${label}.`;
    case "isValidPhone":
      return `Please enter a valid phone number for ${label}.`;
    case "isNumber":
      return `Please enter a number for ${label}.`;
    case "validDays":
      return `Please select at least two days for ${label}.`;
    case "isNotEmpty":
    default:
      return `Please fill in the required field: ${label}.`;
  }
};
